/**
 * Saved views in the left rail (spec §7): name the current URL, list the
 * views of the job (and job-less ones), make one the default for the job,
 * overwrite a view with the current URL, delete; 1..9 open them.
 */
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDeleteView, useSaveView } from "@/api/hooks";
import type { View } from "@/api/types";
import { useShortcuts } from "@/lib/shortcuts";
import { focusCls, rowBarCls } from "@/layout/railStyles";
import { useAllViews } from "./viewsHooks";

function viewsOf(list: readonly View[], jobId: string | undefined): View[] {
  return list.filter((v) => (jobId ? v.job === jobId || !v.job : !v.job)).sort((a, b) => a.id - b.id);
}

export function SavedViews({ jobId }: { jobId: string | undefined }) {
  const views = useAllViews();
  const save = useSaveView();
  const del = useDeleteView();
  const loc = useLocation();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [asDefault, setAsDefault] = useState(false);
  const current = loc.pathname + loc.search;
  const list = viewsOf(views.data?.data ?? [], jobId);

  useShortcuts(
    list.slice(0, 9).map((v, i) => ({
      key: String(i + 1),
      description: `open view "${v.name}"`,
      group: "views",
      handler: () => void navigate(v.url),
    })),
    list.length > 0,
  );

  const create = () => {
    const n = name.trim();
    if (!n) return;
    save.mutate(
      { name: n, job: jobId, url: current, is_default: asDefault },
      {
        onSuccess: () => {
          setCreating(false);
          setName("");
          setAsDefault(false);
        },
      },
    );
  };

  return (
    <>
      <div className="mt-2 mb-0.5 flex items-center px-2 text-3xs font-semibold uppercase tracking-wider text-fg-faint">
        views
        <button
          type="button"
          className="ml-auto text-fg-faint hover:text-fg"
          title="save the current URL as a view"
          aria-label="save current view"
          onClick={() => setCreating((v) => !v)}
        >
          {creating ? "×" : "+"}
        </button>
      </div>
      {creating && (
        <form
          className="mx-2 mb-1 flex flex-col gap-1 rounded-sm border border-border bg-bg-inset/50 p-1"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
        >
          <input
            className="ctl h-5 text-[11px] leading-4"
            placeholder="view name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            aria-label="view name"
          />
          <label className="flex items-center gap-1 text-2xs text-fg-muted">
            <input
              type="checkbox"
              checked={asDefault}
              onChange={(e) => setAsDefault(e.target.checked)}
              disabled={!jobId}
            />
            default for this job
          </label>
          <div className="flex gap-1">
            <button
              type="submit"
              className="ctl on h-5 text-2xs leading-4"
              disabled={save.isPending || !name.trim()}
            >
              save
            </button>
            <button type="button" className="ctl h-5 text-2xs leading-4" onClick={() => setCreating(false)}>
              cancel
            </button>
          </div>
          {save.isError && <div className="text-2xs text-danger">{save.error.message}</div>}
        </form>
      )}
      {views.isError && <div className="px-2 text-fg-faint">views unavailable</div>}
      {list.length === 0 && !views.isError && !creating && (
        <div className="px-2 text-fg-faint">none saved</div>
      )}
      {list.map((v, i) => {
        const active = v.url === current;
        return (
          <div
            key={v.id}
            className={`group relative flex items-center gap-1 rounded-sm px-2 py-0.5 hover:bg-bg-hover narrow:min-h-8 ${active ? rowBarCls : ""}`}
          >
            {i < 9 && <span className="kbd">{i + 1}</span>}
            <button
              type="button"
              className={`min-w-0 flex-1 truncate rounded-sm text-left hover:text-fg ${focusCls} ${active ? "font-semibold text-fg" : "text-fg-muted"}`}
              onClick={() => void navigate(v.url)}
              title={v.url}
              aria-current={active ? "page" : undefined}
            >
              {v.name}
              {!v.job && (
                <span className="ml-1 text-fg-faint" title="not bound to a job">
                  (global)
                </span>
              )}
            </button>
            <button
              type="button"
              className={`${v.is_default ? "text-warn" : "hidden text-fg-faint group-hover:inline hover:text-warn"}`}
              title={v.is_default ? "default view of this job" : "make this the default view of the job"}
              aria-label={v.is_default ? `${v.name} is the default view` : `make ${v.name} the default view`}
              disabled={v.is_default || save.isPending}
              onClick={() =>
                save.mutate({ id: v.id, name: v.name, job: v.job, url: v.url, is_default: true })
              }
            >
              {v.is_default ? "★" : "☆"}
            </button>
            {!active && (
              <button
                type="button"
                className="hidden text-fg-faint group-hover:inline hover:text-fg"
                title="overwrite this view with the current URL"
                aria-label={`update view ${v.name} to the current URL`}
                onClick={() =>
                  save.mutate({ id: v.id, name: v.name, job: v.job, url: current, is_default: v.is_default })
                }
              >
                ↻
              </button>
            )}
            <button
              type="button"
              className="hidden text-fg-faint group-hover:inline hover:text-danger"
              onClick={() => del.mutate(v.id)}
              aria-label={`delete view ${v.name}`}
            >
              ×
            </button>
          </div>
        );
      })}
    </>
  );
}
